/**
 * coralPipeline — bioluminescent coral colony swaying in deep water.
 *
 * 5 coral fronds branch upward from a sand bed at the bottom; each
 * frond recursively splits into 5-6 sub-branches whose tips glow and
 * pulse with the bass. Tiny bioluminescent organisms are sprinkled at
 * pseudo-random positions along each branch; they twinkle independently
 * on the mid/high bands and have a short afterglow.
 *
 * Background is a deep teal/navy gradient + FBM plankton noise, never
 * pure black. A subtle blue caustic pattern overlays the scene to
 * emphasise the underwater feel.
 *
 * Audio is mapped via 3 vec3 uniforms; the audio-react compression
 * matches the rest of NSPP's audio-reactive pack.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CoralPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// --- hashes & noises ---
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)),
           dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise2(p);
    p *= 2.04; a *= 0.5;
  }
  return v;
}

// Distance from point q to segment a-b (clamped to segment).
float distToSeg(vec2 q, vec2 a, vec2 b) {
  vec2 ab = b - a;
  vec2 aq = q - a;
  float u = clamp(dot(aq, ab) / max(dot(ab, ab), 1e-5), 0.0, 1.0);
  return length(q - (a + ab * u));
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  // --- Audio aggregate (matches NSPP audio-reactive pack) ---
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime;

  // --- Background: deep-sea gradient + drifting plankton noise ---
  float vh = (gl_FragCoord.y / uResolution.y);
  vec3 seaTop    = vec3(0.015, 0.045, 0.085);
  vec3 seaBottom = vec3(0.005, 0.020, 0.040);
  vec3 col = mix(seaBottom, seaTop, smoothstep(0.0, 1.0, vh));
  float plank = fbm(p * 3.5 + vec2(t * 0.05, -t * 0.03));
  col += vec3(0.025, 0.040, 0.060) * plank;
  // Light rays / caustic suggestion
  float caustic = fbm(p * 5.0 + vec2(t * 0.15, t * 0.2));
  caustic = pow(caustic, 2.2) * smoothstep(0.45, 1.0, vh);
  col += vec3(0.03, 0.06, 0.09) * caustic;

  // Sway the whole colony gently with bass.
  vec2 globalSway = vec2(sin(t * 0.6) * 0.05, cos(t * 0.45) * 0.02) * bass;

  // --- Sand bed (subtle horizontal grain near the bottom) ---
  float sandY = -0.55 + 0.04 * sin(p.x * 9.0 + t * 0.3);
  float sandMask = smoothstep(0.04, -0.02, p.y - sandY);
  vec3 sandCol = mix(vec3(0.10, 0.07, 0.04), vec3(0.18, 0.13, 0.07),
                      fbm(p * 8.0 + vec2(t * 0.1, 0.0)));
  col = mix(col, sandCol, sandMask * 0.85);

  // --- Coral: 5 fronds; each frond has 5 trunk segments + 4 sub-branches ---
  float wireAccum = 0.0;
  vec3  wireColorAcc = vec3(0.0);
  vec3  tipGlowAcc   = vec3(0.0);
  float organismAccum = 0.0;
  vec3  organismColAcc = vec3(0.0);

  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    vec2 originBase = vec2((hash21(vec2(fi, 11.0)) - 0.5) * 1.6,
                           -0.58 + 0.02 * hash21(vec2(fi, 17.0)));
    vec2 origin = originBase + globalSway;

    // Master frond direction (points upward with slight lean).
    float masterAng = -1.5708 + (hash21(vec2(fi, 23.0)) - 0.5) * 0.7
                    + sin(t * 0.4 + fi * 1.4) * 0.18 * (1.0 + bass);
    vec2 masterDir = vec2(cos(masterAng), sin(masterAng));

    // Trunk: chain of segments so we get a curving frond.
    const int TRUNK = 6;
    vec2 prev = origin;
    for (int j = 0; j < TRUNK; j++) {
      float fj = float(j);
      float r = (fj + 1.0) / float(TRUNK);
      // Local frond direction drifts slightly with time + audio.
      float ang = masterAng
        + sin(t * (1.0 + 0.4 * fi) + fj * 0.8) * 0.18
        + sin(fi * 13.0 + fj * 0.7) * 0.4;
      vec2 dir = vec2(cos(ang), sin(ang));
      float len = 0.10 + 0.04 * sin(fi * 2.3 + fj);
      vec2 next = prev + dir * len;

      // Wireframe distance.
      float wd = distToSeg(p, prev, next);
      float thick = (0.014 - r * 0.008 + bass * 0.005) * (0.7 + 0.6 * (1.0 - r));
      float wire = smoothstep(thick + 0.005, thick - 0.004, wd);
      // Wire color = darker coral pink/violet base.
      vec3 wcol = mix(vec3(0.45, 0.10, 0.28),
                      vec3(0.65, 0.20, 0.55),
                      r);
      wireColorAcc += wcol * wire;
      wireAccum += wire;

      // Sub-branches sprout from selected nodes.
      bool spawnSub = (j == 1 || j == 3 || j == 5);
      if (spawnSub) {
        // Two sub-branches at +/- offsets.
        for (int sb = 0; sb < 2; sb++) {
          float fsb = float(sb);
          float sideSign = (fsb < 0.5) ? -1.0 : 1.0;
          float subAng = ang + sideSign * (0.7 + 0.3 * hash21(vec2(fi, fj + fsb * 3.0)));
          vec2 subDir = vec2(cos(subAng), sin(subAng));
          vec2 subEnd = next + subDir * (0.10 + 0.06 * hash21(vec2(fj, fsb + 1.0)));
          float sd = distToSeg(p, next, subEnd);
          float sthick = 0.008 - r * 0.005;
          float swire = smoothstep(sthick + 0.004, sthick - 0.003, sd);
          vec3 swcol = mix(vec3(0.55, 0.18, 0.40),
                           vec3(0.85, 0.30, 0.70),
                           hash21(vec2(fj + fsb, fi + 1.0)));
          wireColorAcc += swcol * swire;
          wireAccum += swire * 0.7;

          // Glowing tip at the end of the sub-branch.
          float tipD = length(p - subEnd);
          float tipRadius = 0.018 + 0.012 * (1.0 + bass);
          float tipShape = exp(-tipD * tipD * 1.0e3);
          float pulse = 0.5 + 0.5 * sin(t * (2.5 + fi * 0.3 + fj) + fi * 7.0);
          float tipMask = exp(-tipD / tipRadius) * (0.65 + 1.5 * pulse * (0.6 + bass));
          // Tip color cycle through cyan/magenta/gold by frond index.
          vec3 tipColA = vec3(0.10, 0.85, 0.95); // cyan
          vec3 tipColB = vec3(0.95, 0.25, 0.75); // hot pink
          vec3 tipColC = vec3(1.00, 0.75, 0.20); // gold
          vec3 tipCol = mix(tipColA, tipColB, fract(fi * 0.37));
          tipCol = mix(tipCol, tipColC, high * (0.3 + 0.5 * fract(fi * 0.71)));
          tipGlowAcc += tipCol * tipMask;
        }
      }

      // Bioluminescent organisms along the trunk.
      for (int k = 0; k < 3; k++) {
        float fk = float(k);
        vec2 orgPos = mix(prev, next, 0.3 + 0.27 * fk)
                    + vec2(0.018 * sin(fi * 6.0 + fk * 3.0),
                            0.018 * cos(fi * 5.0 + fk * 4.0));
        float od = length(p - orgPos);
        float twinklePhase = t * (3.0 + hash21(vec2(fi * 2.0 + fk, 4.0)) * 4.0)
                            + hash21(vec2(fi * 3.0 + fk, 5.0)) * 30.0;
        float twinkle = pow(0.5 + 0.5 * sin(twinklePhase), 10.0);
        float orgMask = exp(-od * 80.0) * (0.4 + 1.5 * twinkle)
                      * (0.6 + mid * 0.8 + high * 0.5);
        vec3 orgCol = mix(vec3(0.40, 1.0, 0.80),
                          vec3(0.20, 0.65, 1.00),
                          hash21(vec2(fi + fk, 7.0)));
        organismColAcc += orgCol * orgMask;
        organismAccum += orgMask;
      }

      prev = next;
    }
  }

  // Composite wireframe on top of background.
  vec3 wireOut = wireColorAcc / max(1.0, wireAccum * 0.85);
  col = mix(col, wireOut, clamp(wireAccum * 0.7, 0.0, 1.0));

  // Tips and organisms add light.
  col += tipGlowAcc;
  col += organismColAcc;

  // Audio-driven overall glow.
  col += vec3(0.05, 0.20, 0.32) * bass * 0.45
       + vec3(0.30, 0.08, 0.40) * mid  * 0.25
       + vec3(0.15, 0.45, 0.55) * high * 0.20;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeCoralBands(t: number, out: Float32Array): void {
  // bass sub-bands — slow LFO pulsing.
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.1);
  // mid — gentle swaying.
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  // high — bioluminescent twinkle.
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.8 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.5 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.5 + 2.1);
}

export function computeCoralRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  // Same mapping as aurora/hex — 9 logarithmically spaced bands.
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCoralPipeline(gl: WebGL2RenderingContext): CoralPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCoral(
  gl: WebGL2RenderingContext,
  pipe: CoralPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
